import { itemsApi } from "#/api/itemsApi";
import { itemTemplateApi } from "#/api/itemTemplateApi";
import { productApi } from "#/api/productApi";
import { rfidTagApi } from "#/api/rfidTagApi";
import { sitesApi } from "#/api/sitesApi";
import ActionButton from "#/components/buttons/ActionButton";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import {
  type CreateItemPayload,
  type Item,
  ItemType,
  MaintenanceStatus,
  RetrievalStatus,
} from "#/types/items";
import { logger } from "#/utils/logger";
import { getRandomColor } from "#/utils/ColorUtils";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { getDwellStatus } from "#/utils/dwell";
import { formatCurrency } from "#/utils/formatCurrency";
import { useClient } from "#/hook/useClient";
import {
  Accordion,
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconAlertTriangle, IconExternalLink, IconEye, IconHelp, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import HistoricalDataTable from "./components/HistoricalDataTable";
import LastKnownPositionSection from "./components/LastKnownPositionSection";
import RelatedItemsSection from "./components/RelatedItemsSection";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useUnsavedChangesWarning } from "#/hooks/useUnsavedChangesWarning";
import { useShipmentManifestsEnabled } from "#/hooks/useShipmentManifestsEnabled";
import { manifestApi } from "#/api/manifestApi";
import type { HawbOption, ItemLinkWarning } from "#/types/manifest";
import HawbLinkWarningsModal from "#/components/manifests/HawbLinkWarningsModal";
import ShipmentManifestSection from "./components/ShipmentManifestSection";

/** What the form holds for a stored item. Empty values load as empty, never as a fake default. */
const toFormValues = (item: Item): CreateItemPayload => ({
  rfid_ids: item.rfid_tags?.map((tag) => tag.id) ?? [],
  site_id: item.site?.id != null ? String(item.site.id) : null,
  product_id: item.product?.id != null ? String(item.product.id) : null,
  // `new Date(null)` is 1 January 1970; an empty date must stay empty.
  date_of_circulation: item.date_of_circulation ? new Date(item.date_of_circulation) : null,
  notify_email: item.notify_email ?? "",
  external_id: item.external_id ?? "",
  next_maintenance_date: item.next_maintenance_date ? new Date(item.next_maintenance_date) : null,
  last_maintenance_date: item.last_maintenance_date ? new Date(item.last_maintenance_date) : null,
  location: item.location ?? "",
  customize_columns: item.customize_columns ?? {},
  stolen: item.stolen ?? false,
  status: item.status ?? null,
  maintenance_status: item.maintenance_status ?? null,
  retrieval_status: item.retrieval_status ?? null,
  type: item.type ?? ItemType.ITEM,
  cage_item_ids: item.cage_items?.map((ci) => ci.id) ?? [],
});

/** A date input value as the API wants it, or null to clear it. */
const toApiDate = (value: Date | string | null | undefined): string | null =>
  value ? moment(value).format("YYYY-MM-DD") : null;

const ItemDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { canWrite, canDelete } = usePermission();
  const { client } = useClient();
  const [searchParams] = useSearchParams();

  const [type, setType] = useState<string>(t("items.scan"));

  const [
    showHistoricalDrawer,
    { open: openHistoricalDrawer, close: closeHistoricalDrawer },
  ] = useDisclosure(false);

  const [
    templateModalOpened,
    { open: openTemplateModal, close: closeTemplateModal },
  ] = useDisclosure(false);

  const [templateName, setTemplateName] = useState<string>("");
  const [templateDescription, setTemplateDescription] = useState<string>("");

  const emptyForm = {
    rfid_ids: [],
    product_id: undefined,
    site_id: null,
    date_of_circulation: null,
    notify_email: "",
    external_id: "",
    next_maintenance_date: null,
    last_maintenance_date: null,
    location: "",
    customize_columns: {},
    // Never preselected: status is one of the category's scan steps and is the
    // user's choice; the other two are system-tracked and defaulted by the backend.
    status: null,
    maintenance_status: null,
    retrieval_status: null,
    stolen: false,
    type: ItemType.ITEM,
    cage_item_ids: [],
  };

  // Scan-step labels of the currently selected category, kept in a ref so the
  // form's validate rules (closures created once) always see the latest set.
  const categoryStepLabelsRef = useRef<Set<string> | null>(null);

  const form = useForm<CreateItemPayload>({
    initialValues: emptyForm,
    validate: {
      notify_email: (value) => {
        if (value && !/^\S+@\S+\.\S+$/.test(value)) {
          return "Invalid email";
        }
        return null;
      },
      status: (value, values) => {
        // No category: an item cannot have a status, nothing to validate.
        if (!values.product_id) return null;
        if (!value) return t("items.statusRules.statusRequired");
        const steps = categoryStepLabelsRef.current;
        // Steps still loading: let the backend be the judge rather than block.
        if (steps === null) return null;
        return steps.has(value)
          ? null
          : `${sanitizeItemStatusLabel(value)} — ${t("items.statusRules.statusNotInCategory")}`;
      },
    },
    transformValues: (values) => ({
      ...values,
      date_of_circulation: toApiDate(values.date_of_circulation),
      next_maintenance_date: toApiDate(values.next_maintenance_date),
      last_maintenance_date: toApiDate(values.last_maintenance_date),
    }),
  });

  const manifestsEnabled = useShipmentManifestsEnabled();
  /**
   * Consignment change waiting for Save: undefined = unchanged, null = unlink,
   * an option = link to it. Kept outside the form because it is written
   * through its own endpoint, after the backend's checks are confirmed.
   */
  const [pendingHawb, setPendingHawb] = useState<HawbOption | null | undefined>(undefined);
  /** Preview warnings waiting for the user's confirmation, and what to run after. */
  const [hawbConfirm, setHawbConfirm] = useState<{
    itemId: number;
    hawbId: number | null;
    warnings: ItemLinkWarning[];
    onDone: () => void;
    onCancel: () => void;
  } | null>(null);
  const [hawbLinkBusy, setHawbLinkBusy] = useState(false);

  useUnsavedChangesWarning(form.isDirty() || pendingHawb !== undefined);

  const { data: { products = [] } = {} } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await productApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }
      return {
        products: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await sitesApi.getAll({ page: 1, limit: 10000 });
      if (!response.data) {
        throw new Error("Failed to fetch sites");
      }
      return {
        sites: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });


  const selectedProductId = form.values.product_id ? Number(form.values.product_id) : null;

  // The selected category's scan steps: the only statuses an item may have.
  const { data: categorySteps, isPending: categoryStepsPending } = useQuery({
    queryKey: ["product-statuses", selectedProductId],
    queryFn: async () => {
      const response = await productApi.getStatuses(selectedProductId as number);
      if (!response.data) {
        throw new Error("Failed to fetch category scan steps");
      }
      return [...response.data].sort((a, b) => a.step - b.step);
    },
    enabled: selectedProductId !== null,
    staleTime: 1000 * 60,
  });
  const stepsLoaded = selectedProductId !== null && !categoryStepsPending;
  const categoryStepLabels = useMemo(() => {
    if (!stepsLoaded) return null;
    return new Set((categorySteps ?? []).map((step) => step.label).filter((label): label is string => !!label));
  }, [stepsLoaded, categorySteps]);
  categoryStepLabelsRef.current = selectedProductId === null ? new Set() : categoryStepLabels;

  // After the USER changes the category, a status that is not a step of the new
  // category is dropped once the steps are known. Loading an existing item never
  // triggers this: its (possibly wrong) status must stay visible so it can be fixed.
  const pendingStatusRecheckRef = useRef(false);
  const [showCategoryClearedHint, setShowCategoryClearedHint] = useState(false);
  useEffect(() => {
    if (!pendingStatusRecheckRef.current || !categoryStepLabels) return;
    pendingStatusRecheckRef.current = false;
    const current = form.values.status;
    if (current && !categoryStepLabels.has(current)) {
      form.setFieldValue("status", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the steps for the new category arrive
  }, [categoryStepLabels]);

  const handleCategoryChange = (value: string | null) => {
    setShowCategoryClearedHint(false);
    if (value === null) {
      // No category, no status: a status only exists as one of the category's steps.
      if (form.values.status) setShowCategoryClearedHint(true);
      form.setFieldValue("status", null);
      form.setFieldValue("product_id", null);
      return;
    }
    pendingStatusRecheckRef.current = true;
    form.setFieldValue("product_id", value);
  };

  const statusOptions = useMemo(() => {
    const options = (categorySteps ?? [])
      .filter((step) => !!step.label)
      .map((step) => ({ value: step.label, label: sanitizeItemStatusLabel(step.label) }));
    const current = form.values.status;
    // A stored status that is not a step of its category stays selectable, flagged, so it can be fixed.
    if (current && stepsLoaded && !options.some((option) => option.value === current)) {
      options.push({
        value: current,
        label: `${sanitizeItemStatusLabel(current)} (${t("items.statusRules.statusNotInCategory")})`,
      });
    }
    return options;
  }, [categorySteps, form.values.status, stepsLoaded, t]);
  const categoryHasNoSteps = stepsLoaded && (categorySteps?.length ?? 0) === 0;

  const templateId = searchParams.get("template");

  const templateQuery = useQuery({
    queryKey: ["template-for-item", templateId],
    queryFn: async () => {
      if (!templateId || id !== "new") {
        return;
      }
      const response = await itemTemplateApi.getById(templateId);
      if (!response.data) {
        throw new Error("Failed to fetch template");
      }

      const template = response.data;
      form.setValues({
        rfid_ids: [],
        site_id: template?.site?.id.toString() ?? null,
        product_id: template?.product?.id.toString() ?? undefined,
        date_of_circulation: null,
        notify_email: template.notify_email ?? "",
        external_id: "",
        next_maintenance_date: null,
        last_maintenance_date: null,
        location: template.location ?? "",
        customize_columns: template.customize_columns ?? {},
        status: template.status ?? null,
        maintenance_status: template.maintenance_status ?? null,
        retrieval_status: template.retrieval_status ?? null,
        stolen: false,
        type: (template.type as ItemType) ?? ItemType.ITEM,
        cage_item_ids: [],
      });
      form.resetDirty();

      return response.data;
    },
    enabled: id === "new" && templateId !== null,
  });

  const itemQuery = useQuery({
    queryKey: ["item-details", id],
    queryFn: async () => {
      if (!id || id === "new") {
        return;
      }
      const response = await itemsApi.getById(id);
      if (!response.data) {
        throw new Error("Failed to fetch item");
      }

      return response.data;
    },
    enabled: id !== undefined && id !== "new",
  });

  const storedHawb = itemQuery.data?.hawb ?? null;
  const hawbChanged =
    manifestsEnabled &&
    pendingHawb !== undefined &&
    (pendingHawb?.id ?? null) !== (storedHawb?.id ?? null);

  /** Writes the item ↔ consignment link, then refreshes the item. */
  const applyHawbLink = async (itemId: number, hawbId: number | null): Promise<boolean> => {
    setHawbLinkBusy(true);
    try {
      await manifestApi.itemLink([itemId], hawbId);
      setPendingHawb(undefined);
      void queryClient.invalidateQueries({ queryKey: ["item-details", String(itemId)] });
      return true;
    } catch (error) {
      // The API client already shows the error toast.
      logger.error("Failed to update the item's consignment link:", error);
      return false;
    } finally {
      setHawbLinkBusy(false);
    }
  };

  /**
   * Runs the backend checks first. With warnings the user must confirm before
   * anything changes; without, the link is written straight away. `onDone`
   * runs only after the link was written.
   */
  const startHawbLink = async (
    itemId: number,
    hawbId: number | null,
    onDone: () => void,
    onCancel: () => void,
  ): Promise<void> => {
    setHawbLinkBusy(true);
    let warnings: ItemLinkWarning[];
    try {
      const response = await manifestApi.itemLinkPreview([itemId], hawbId);
      warnings = response.data.warnings ?? [];
    } catch (error) {
      logger.error("Failed to check the item's consignment link:", error);
      setHawbLinkBusy(false);
      onCancel();
      return;
    }
    setHawbLinkBusy(false);
    if (warnings.length > 0) {
      setHawbConfirm({ itemId, hawbId, warnings, onDone, onCancel });
      return;
    }
    if (await applyHawbLink(itemId, hawbId)) onDone();
    else onCancel();
  };

  const [rfidTagSearch, setRfidTagSearch] = useState("");
  const [debouncedRfidTagSearch] = useDebouncedValue(rfidTagSearch, 400);
  const [selectedRfidTags, setSelectedRfidTags] = useState<{ id: number; rfid: string }[]>([]);

  // Load the fetched item into the form once, and never over unsaved edits:
  // React Query refetches on window focus, which used to wipe what the user
  // was typing. While the form is dirty the fetched object waits; it is
  // applied after the next save makes the form clean again.
  const appliedItemRef = useRef<Item | null>(null);
  useEffect(() => {
    const item = itemQuery.data;
    if (!item || appliedItemRef.current === item || form.isDirty()) return;
    const values = toFormValues(item);
    setSelectedRfidTags((item.rfid_tags ?? []).map((tag) => ({ id: tag.id, rfid: tag.rfid })));
    form.setInitialValues(values);
    form.setValues(values);
    form.resetDirty(values);
    appliedItemRef.current = item;
  }, [itemQuery.data, form]);

  const { data: { searchResults = [] } = {}, isLoading: rfidTagsLoading } = useQuery({
    queryKey: ["rfid-tags-search", debouncedRfidTagSearch],
    queryFn: async () => {
      const response = await rfidTagApi.getAll({
        linked: false,
        limit: 20,
        search: debouncedRfidTagSearch,
      });
      return { searchResults: response.data.data || [] };
    },
    enabled: debouncedRfidTagSearch.length >= 2,
  });

  const rfidTagOptions = useMemo(() => {
    const selectedIds = new Set(selectedRfidTags.map((t) => t.id));
    const fromSearch = searchResults.filter((t) => !selectedIds.has(t.id));
    return [...selectedRfidTags, ...fromSearch].map((t) => ({
      label: t.rfid,
      value: t.id.toString(),
    }));
  }, [selectedRfidTags, searchResults]);

  const [cageItemSearch, setCageItemSearch] = useState<string>("");
  /** Relations chosen while creating: written once the item has an id. */
  const [pendingRelatedItems, setPendingRelatedItems] = useState<Item[]>([]);
  const [debouncedCageItemSearch] = useDebouncedValue(cageItemSearch, 400);

  const {
    data: { cageItems: availableCageItems = [] } = {},
    isLoading: cageItemsLoading,
    refetch: refetchCageItems,
  } = useQuery({
    queryKey: ["available-cage-items", id, debouncedCageItemSearch, itemQuery.data?.id],
    queryFn: async () => {
      const response = await itemsApi.getAvailableCageItems({
        search: debouncedCageItemSearch || undefined,
        cage_id: id !== "new" ? Number(id) : undefined,
        page: 1,
        limit: 10000,
      });

      return { cageItems: response.data.data || [] };
    },
    enabled: form.values.type === ItemType.CAGE,
    staleTime: 1000 * 60,
  });

  const inlineCageItemMutation = useMutation({
    mutationFn: async (externalId: string) => {
      const productId = form.values.product_id
        ? Number(form.values.product_id)
        : undefined;
      if (!productId) {
        throw new Error(t("items.inlineCageItem.selectCategoryFirst"));
      }
      return await itemsApi.create({
        external_id: externalId,
        product_id: productId,
        type: ItemType.CAGE_ITEM,
      });
    },
    onSuccess: (data) => {
      const newItem = data.data;
      const currentIds = form.values.cage_item_ids ?? [];
      form.setFieldValue("cage_item_ids", [...currentIds, newItem.id]);
      setCageItemSearch("");
      void refetchCageItems();
      showSuccessNotification({
        message: t("items.inlineCageItem.createdSuccess", { externalId: newItem.external_id }),
      });
    },
    onError: (error) => {
      showErrorNotification({
        message: error.message ?? t("items.inlineCageItem.createdError"),
      });
    },
  });

  /**
   * The request body for a save. The rule is simple: `undefined` = untouched,
   * dropped; everything else is sent, including `null` and "" which the backend
   * treats as "clear this field". Before, emptied fields were stripped and the
   * old value silently survived in the database.
   *
   * On create nothing can be cleared yet, so empty values are dropped there,
   * and the two system-tracked statuses are left to the backend's defaults.
   */
  const toSavePayload = (values: typeof form.values, mode: "create" | "update"): Partial<CreateItemPayload> => {
    const payload: Partial<CreateItemPayload> = {};
    const isEmpty = (value: unknown) =>
      value === null || value === "" || (Array.isArray(value) && value.length === 0);

    (Object.entries(values) as [keyof CreateItemPayload, CreateItemPayload[keyof CreateItemPayload]][]).forEach(([key, value]) => {
      if (value === undefined) return;
      if (mode === "create" && isEmpty(value)) return;

      if (key === "site_id" || key === "product_id") {
        payload[key] = value === null || value === "" ? null : Number(value);
        return;
      }
      (payload as Record<string, CreateItemPayload[keyof CreateItemPayload]>)[key as string] = value;
    });

    if (mode === "create") {
      // A status only exists as a step of a category.
      if (payload.product_id == null) delete payload.status;
      // System-tracked statuses: never a user choice on create.
      if (payload.retrieval_status == null) delete payload.retrieval_status;
      if (payload.maintenance_status == null) delete payload.maintenance_status;
    } else {
      // The external id identifies the item; an emptied field is not a clear.
      if (payload.external_id === "") delete payload.external_id;
      if (payload.product_id === null) payload.status = null;
    }

    return payload;
  };

  const handleSubmit = (values: typeof form.values) => {
    const payload = toSavePayload(values, id === "new" ? "create" : "update");
    // A new item has no id yet: its consignment is linked after create.
    if (id !== "new" && hawbChanged) {
      const itemId = Number(id);
      const formDirty = form.isDirty();
      void startHawbLink(
        itemId,
        pendingHawb?.id ?? null,
        () => {
          if (formDirty) {
            itemMutation.mutate(payload as CreateItemPayload);
          } else {
            showSuccessNotification({ message: t("items.hawbLink.saved") });
          }
        },
        () => undefined,
      );
      return;
    }
    itemMutation.mutate(payload as CreateItemPayload);
  };

  const itemMutation = useMutation({
    mutationFn: async (payload: CreateItemPayload) => {
      if (id !== "new") {
        return await itemsApi.update(Number(id), payload);
      } else {
        return await itemsApi.create(payload);
      }
    },
    onSuccess: async (data) => {
      if (id === "new") {
        // Relations picked before the item existed can only be written now.
        if (pendingRelatedItems.length > 0) {
          try {
            await itemsApi.linkRelatedItems(
              data.data.id,
              pendingRelatedItems.map((item) => item.id),
            );
          } catch (error) {
            logger.error("Failed to link related items after create:", error);
          }
        }
        const newItemId = data.data.id;
        const goToItem = () => void navigate(`/inventory/items/${newItemId}`);
        if (manifestsEnabled && pendingHawb) {
          await startHawbLink(newItemId, pendingHawb.id, goToItem, () => {
            showErrorNotification({ message: t("items.hawbLink.notLinkedAfterCreate") });
            goToItem();
          });
        } else {
          goToItem();
        }
      } else {
        // What the form holds is now what is stored: make it the clean baseline,
        // then let the refetched item flow in through the apply effect.
        form.setInitialValues(form.values);
        form.resetDirty(form.values);
        setShowCategoryClearedHint(false);
        void queryClient.invalidateQueries({ queryKey: ["item-details", id] });
      }
      showSuccessNotification({
        message: `Item ${id === "new" ? "created" : "updated"} successfully`,
      });
    },
    onError: (error) => {
      showErrorNotification({
        message: error.message ?? "Failed to process. Please try again later",
      });
    },
  });

  const createAndSaveAsTemplateMutation = useMutation({
    mutationFn: async ({
      itemPayload,
      templateName,
      templateDescription,
    }: {
      itemPayload: CreateItemPayload;
      templateName: string;
      templateDescription: string;
    }) => {
      const itemResponse = await itemsApi.create(itemPayload);

      const templatePayload = {
        name: templateName,
        description: templateDescription || undefined,
        product_id: itemPayload.product_id ? Number(itemPayload.product_id) : null,
        site_id: itemPayload.site_id ? Number(itemPayload.site_id) : null,
        location: itemPayload.location || undefined,
        notify_email: itemPayload.notify_email || undefined,
        status: itemPayload.status || undefined,
        retrieval_status: itemPayload.retrieval_status || undefined,
        maintenance_status: itemPayload.maintenance_status || undefined,
        customize_columns: itemPayload.customize_columns || undefined,
        type: itemPayload.type || undefined,
      };

      await itemTemplateApi.create(templatePayload);

      return itemResponse;
    },
    onSuccess: (data) => {
      void navigate(`/inventory/items/${data.data.id}`);
      showSuccessNotification({
        message: "Item created and saved as template successfully",
      });
      closeTemplateModal();
    },
    onError: (error) => {
      showErrorNotification({
        message: error.message ?? "Failed to process. Please try again later",
      });
    },
  });

  return (
    <DetailPageLayout
      title={id != "new" ? t("items.itemDetails") : t("items.createItem")}
      loading={itemMutation.isPending || itemQuery.isLoading || templateQuery.isLoading}
      navRightSection={
        <div className={`w-full flex gap-2 items-center`}>
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty() && pendingHawb === undefined}
            onClick={() => {
              form.reset();
              setPendingHawb(undefined);
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={itemMutation.isPending || hawbLinkBusy}
              disabled={!(form.isDirty() || hawbChanged) || !canWrite()}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {id === "new" ? t("common.create") : t("common.save")}
            </Button>
          </PermissionTooltip>

          {id === "new" && (
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                color="blue"
                variant="filled"
                disabled={!form.isDirty() || !canWrite()}
                onClick={() => {
                  form.onSubmit(() => {
                    setTemplateName(`Template from item ${new Date().toISOString()}`);
                    setTemplateDescription(`Auto-generated template from item creation`);
                    openTemplateModal();
                  })();
                }}
                size="xs"
              >
                {t("items.createAndSaveAsTemplate")}
              </Button>
            </PermissionTooltip>
          )}

          {id !== "new" && (
            <div className="flex items-center gap-4 pl-2">
              <Divider orientation="vertical" />
              <Button
                variant="outline"
                color="violet"
                leftSection={<IconEye size={16} />}
                onClick={openHistoricalDrawer}
                size="xs"
              >
                {t("common.historicalData")}
              </Button>
            </div>
          )}
        </div>
      }
    >
      <div className="w-full flex flex-col">
        {(() => {
          const detailItem = itemQuery.data;
          if (!detailItem) return null;
          const dwell = getDwellStatus(detailItem, client);
          if (!dwell.isStuck) return null;
          return (
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
              <IconAlertTriangle size={20} className="mt-0.5 shrink-0 text-red-600" />
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-red-700">
                  {t("items.dwell.cardTitle")}
                </span>
                <span className="text-sm text-red-700">
                  {t("items.dwell.cardMessage", {
                    days: dwell.idleDays,
                    status: sanitizeItemStatusLabel(detailItem.status),
                    dwell: dwell.dwellDays,
                  })}
                </span>
                <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-sm text-red-800">
                  <span>
                    <span className="font-medium">{t("items.dwell.length")}:</span>{" "}
                    {t("common.days", { count: dwell.idleDays ?? 0 })}
                  </span>
                  <span>
                    <span className="font-medium">{t("items.dwell.location")}:</span>{" "}
                    {detailItem.site?.name || detailItem.location || "--"}
                  </span>
                  <span>
                    <span className="font-medium">{t("items.dwell.value")}:</span>{" "}
                    {detailItem.product?.cost != null
                      ? formatCurrency(detailItem.product.cost)
                      : "--"}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
        <form
          className="space-y-8 relative"
          onSubmit={form.onSubmit(handleSubmit)}
        >
          <Accordion
            variant={"separated"}
            defaultValue={["basic", "shipment_manifest", "position", "customize_columns", "cage_items"]}
            multiple
          >
            <Accordion.Item value={"basic"}>
              <Accordion.Control>{t("common.basicDetails")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full grid grid-cols-1 gap-4">
                  <div className="w-full grid grid-cols-2 gap-4">
                    <Select
                      label={t("items.form.type")}
                      data={[
                        { label: t("items.types.item"), value: ItemType.ITEM },
                        { label: t("items.types.cage"), value: ItemType.CAGE },
                        { label: t("items.types.cageItem"), value: ItemType.CAGE_ITEM },
                      ]}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      {...form.getInputProps("type")}
                      onChange={(value) => {
                        form.setFieldValue("type", (value as ItemType) ?? ItemType.ITEM);
                        if (value !== ItemType.CAGE) {
                          form.setFieldValue("cage_item_ids", []);
                        }
                      }}
                    />

                    <TextInput
                      label={t("items.form.externalId")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder={t("items.form.externalIdPlaceholder")}
                      {...form.getInputProps("external_id")}
                    />
                  </div>

                  <MultiSelect
                    label={t("items.form.rfidTags")}
                    placeholder="Type to search RFID tags"
                    searchable
                    searchValue={rfidTagSearch}
                    onSearchChange={setRfidTagSearch}
                    disabled={
                      !canWrite() ||
                      itemQuery.isLoading ||
                      itemMutation.isPending
                    }
                    data={rfidTagOptions}
                    value={form.values.rfid_ids?.map((id) => id.toString()) ?? []}
                    onChange={(values) => {
                      const numericIds = values.map((v) => Number(v));
                      // Update selWHAected tags state from current options
                      const allTags = [...selectedRfidTags, ...searchResults];
                      setSelectedRfidTags(
                        numericIds.map((nid) => {
                          const tag = allTags.find((t) => t.id === nid);
                          return { id: nid, rfid: tag?.rfid ?? nid.toString() };
                        })
                      );
                      form.setFieldValue("rfid_ids", numericIds);
                    }}
                    clearable
                    filter={({ options }) => options}
                    nothingFoundMessage={
                      rfidTagSearch.length < 2
                        ? "Type at least 2 characters to search"
                        : rfidTagsLoading
                          ? "Searching..."
                          : "No RFID tags found"
                    }
                  />

                  <div className="w-full grid grid-cols-3 gap-4">
                    <div className="w-full flex items-center relative">
                      <div className="w-full">
                        <Select
                          label={t("items.form.site")}
                          placeholder={t("items.form.sitePlaceholder")}
                          searchable
                          disabled={
                            !canWrite() ||
                            itemQuery.isLoading || itemMutation.isPending
                          }
                          data={sites.map((site) => {
                            return {
                              label: site?.name ?? "",
                              value: site?.id?.toString() ?? "",
                            };
                          })}
                          {...form.getInputProps("site_id")}
                        />
                      </div>

                      <div className="a absolute top-0 right-0">
                        <Tooltip
                          label="Site is a required field. An item must belong to a site"
                          withArrow
                        >
                          <ActionIcon variant="subtle" color="gray">
                            <IconHelp size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </div>
                    </div>

                    <div className="w-full flex items-center relative">
                      <div className="w-full">
                        <Select
                          label={t("items.form.category")}
                          placeholder={t("items.form.categoryPlaceholder")}
                          searchable
                          disabled={
                            !canWrite() ||
                            itemQuery.isLoading || itemMutation.isPending
                          }
                          data={products.map((prod) => {
                            return {
                              label: prod?.name ?? "",
                              value: prod?.id?.toString() ?? "",
                            };
                          })}
                          clearable
                          value={form.values.product_id != null ? String(form.values.product_id) : null}
                          error={form.errors.product_id}
                          onChange={handleCategoryChange}
                        />
                      </div>

                      <div className="a absolute top-0 right-0">
                        <Tooltip
                          label="Category is a required field. It is a classification of the item that you are about to create. It contains essential information like type, cost etc."
                          withArrow
                          multiline
                          w={220}
                        >
                          <ActionIcon variant="subtle" color="gray">
                            <IconHelp size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <Select
                        label={t("items.form.status")}
                        placeholder={
                          selectedProductId === null
                            ? t("items.statusRules.statusNeedsCategory")
                            : t("items.form.statusPlaceholder")
                        }
                        searchable
                        disabled={!canWrite() || selectedProductId === null || categoryHasNoSteps}
                        data={statusOptions}
                        clearable
                        withAsterisk={selectedProductId !== null}
                        value={form.values.status}
                        error={form.errors.status}
                        onChange={(value) => form.setFieldValue("status", value)}
                      />
                      {categoryHasNoSteps && (
                        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                          <IconAlertTriangle size={14} className="mt-0.5 shrink-0" />
                          <span>
                            {t("items.statusRules.categoryHasNoSteps")}{" "}
                            <a
                              href={`/inventory/categories/${selectedProductId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold underline underline-offset-2"
                            >
                              {t("items.statusRules.openCategory")}
                            </a>
                          </span>
                        </div>
                      )}
                      {showCategoryClearedHint && selectedProductId === null && (
                        <span className="text-xs text-amber-700">
                          {t("items.statusRules.categoryClearedStatus")}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-3 gap-4">
                    <DateInput
                      label={t("items.form.dateOfCirculation")}
                      placeholder="Select a date of circulation"
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      {...form.getInputProps("date_of_circulation")}
                    />

                    <DateInput
                      label={t("items.form.lastMaintenanceDate")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder="Select a last maintenance date"
                      maxDate={new Date()}
                      {...form.getInputProps("last_maintenance_date")}
                    />

                    <DateInput
                      label={t("items.form.nextMaintenanceDate")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder="Select a next maintenance date"
                      minDate={new Date()}
                      {...form.getInputProps("next_maintenance_date")}
                    />
                  </div>

                  <div className="w-full grid grid-cols-2 gap-4">
                    <Select
                      label={t("items.form.retrievalStatus")}
                      placeholder={t("items.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={Object.values(RetrievalStatus).map((status) => ({
                        label: sanitize(status),
                        value: status,
                      }))}
                      clearable
                      {...form.getInputProps("retrieval_status")}
                    />

                    <Select
                      label={t("items.form.maintenanceStatus")}
                      placeholder={t("items.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={Object.values(MaintenanceStatus).map((status) => ({
                        label: sanitize(status),
                        value: status,
                      }))}
                      clearable
                      {...form.getInputProps("maintenance_status")}
                    />
                  </div>

                  <div className="w-full grid grid-cols-2 gap-4">
                    <TextInput
                      label={t("items.form.location")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder={t("items.form.locationPlaceholder")}
                      {...form.getInputProps("location")}
                    />

                    <TextInput
                      label={t("items.form.manufacturerEmail")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder={t("items.form.manufacturerEmailPlaceholder")}
                      type="email"
                      {...form.getInputProps("notify_email")}
                    />
                  </div>
                </div>

                {id !== "new" && (
                  <div className="border border-slate-300 rounded-lg bg-slate-50 p-4 mt-4">
                    <PermissionTooltip disabled={!canWrite()}>
                      <Checkbox
                        label={t("items.form.markAsStolen")}
                        color="red.5"
                        disabled={itemQuery.isLoading || itemMutation.isPending || !canWrite()}
                        checked={form.values.stolen}
                        classNames={{
                          label: "font-bold text-red-500 text-base",
                        }}
                        size="sm"
                        description="This will flag the item as stolen. Next time someone scans or brings it to a filling station, it will notify the client by email and will also display red flag on the filling station display "
                        {...form.getInputProps("stolen")}
                      />
                    </PermissionTooltip>
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>

            {manifestsEnabled && (
              <ShipmentManifestSection
                storedHawb={storedHawb}
                pendingHawb={pendingHawb}
                onPendingChange={setPendingHawb}
                disabled={itemQuery.isLoading || itemMutation.isPending || hawbLinkBusy}
              />
            )}

            {id !== "new" && (
              <Accordion.Item value="position">
                <Accordion.Control>{t("items.position.title")}</Accordion.Control>
                <Accordion.Panel>
                  <LastKnownPositionSection item={itemQuery.data} />
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {form.values.type === ItemType.CAGE && (
              <Accordion.Item value="cage_items">
                <Accordion.Control>{t("items.cageItems")}</Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label={t("items.form.cageItems")}
                    placeholder={
                      cageItemsLoading
                        ? t("common.loading")
                        : t("items.form.cageItemsPlaceholder")
                    }
                    searchable
                    hidePickedOptions
                    disabled={
                      !canWrite() ||
                      itemQuery.isLoading ||
                      itemMutation.isPending
                    }
                    data={availableCageItems.map((ci) => ({
                      label: `${ci.external_id || `ID: ${ci.id}`}${ci.rfid_tags && ci.rfid_tags.length > 0 ? ` (${ci.rfid_tags[0].rfid})` : ""}`,
                      value: ci.id.toString(),
                    }))}
                    value={
                      form.values.cage_item_ids?.map((cid) => cid.toString()) ?? []
                    }
                    onChange={(values) => {
                      form.setFieldValue(
                        "cage_item_ids",
                        values.map((v) => Number(v)),
                      );
                    }}
                    onSearchChange={setCageItemSearch}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        cageItemSearch &&
                        !availableCageItems.some(
                          (ci) => ci.external_id === cageItemSearch,
                        ) &&
                        form.values.product_id &&
                        !inlineCageItemMutation.isPending
                      ) {
                        e.preventDefault();
                        inlineCageItemMutation.mutate(cageItemSearch);
                      }
                    }}
                    clearable
                    nothingFoundMessage={
                      cageItemSearch ? (
                        <Button
                          variant="light"
                          color="blue"
                          size="xs"
                          fullWidth
                          loading={inlineCageItemMutation.isPending}
                          onClick={() => {
                            inlineCageItemMutation.mutate(cageItemSearch);
                          }}
                        >
                          {t("items.form.createCageItemInline", {
                            externalId: cageItemSearch,
                          })}
                        </Button>
                      ) : (
                        t("items.form.noCageItemsAvailable")
                      )
                    }
                  />
                  {(form.values.cage_item_ids ?? []).length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-bold text-slate-600">{t("items.currentlyLinkedItems")}</span>
                        <span className="text-xs text-slate-600">{t("items.clickOnItemsToSeeDetails")}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(form.values.cage_item_ids ?? []).map((cid) => {
                          const ci = availableCageItems.find((c) => c.id === cid);
                          const label = ci
                            ? `${ci.external_id || `ID: ${ci.id}`}${ci.rfid_tags && ci.rfid_tags.length > 0 ? ` (${ci.rfid_tags[0].rfid})` : ""}`
                            : `ID: ${cid}`;
                          return (
                            <Badge
                              key={cid}
                              color={getRandomColor(label)}
                              variant="light"
                              size="lg"
                              className="cursor-pointer hover:opacity-80 transition-opacity"
                              rightSection={<IconExternalLink size={12} className="opacity-60" />}
                              onClick={() => window.open(`/inventory/items/${cid}`, '_blank')}
                            >
                              {label}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            )}

            <RelatedItemsSection
              itemId={id && id !== "new" && Number.isInteger(Number(id)) ? Number(id) : null}
              pendingItems={pendingRelatedItems}
              onPendingItemsChange={setPendingRelatedItems}
            />

            <Accordion.Item value="customize_columns">
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>{t("common.customFields")}</span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size="xs"
                      icon={IconPlus}
                      disabled={!canWrite()}
                      onClick={(e) => {
                        e.stopPropagation();
                        const existing = { ...form.values.customize_columns };
                        let newKey = `field_${Object.keys(existing).length + 1}`;
                        while (newKey in existing) {
                          newKey += "_";
                        }
                        form.setFieldValue("customize_columns", {
                          ...existing,
                          [newKey]: "",
                        });
                      }}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>

              <Accordion.Panel>
                {Object.entries(form.values.customize_columns ?? {}).length <=
                0 ? (
                  <div>{t("common.addCustomFieldHint")}</div>
                ) : (
                  <div className="w-full flex flex-col gap-2">
                    {Object.entries(form.values.customize_columns ?? {}).map(
                      ([key, value], index) => {
                        const fieldId = `${key}_${index}`;

                        return (
                          <div
                            key={fieldId}
                            className="w-full grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center"
                          >
                            {/* Editable Key input */}
                            <span className="bg-black/5 font-bold text-black/70 rounded-full p-2 w-8 h-8 flex items-center justify-center">
                              <span>{index + 1}</span>
                            </span>
                            <TextInput
                              defaultValue={key}
                              disabled={!canWrite()}
                              onBlur={(event) => {
                                const newKey = event.currentTarget.value.trim();
                                if (!newKey || newKey === key) return;

                                const updated = {
                                  ...form.values.customize_columns,
                                };
                                const val = updated[key];
                                delete updated[key];

                                if (newKey in updated) return; // avoid duplicates

                                updated[newKey] = val;
                                form.setFieldValue(
                                  "customize_columns",
                                  updated
                                );
                              }}
                            />

                            {/* Editable Value input */}
                            <TextInput
                              value={value}
                              disabled={!canWrite()}
                              onChange={(event) => {
                                form.setFieldValue(
                                  `customize_columns.${key}`,
                                  event.currentTarget.value
                                );
                              }}
                            />

                            {/* Delete Button */}
                            <PermissionTooltip disabled={!canDelete()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                color="red.5"
                                disabled={!canDelete()}
                                onClick={() => {
                                  const updated = {
                                    ...form.values.customize_columns,
                                  };
                                  delete updated[key];
                                  form.setFieldValue(
                                    "customize_columns",
                                    updated
                                  );
                                }}
                              />
                            </PermissionTooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>

            <Drawer
              opened={showHistoricalDrawer}
              onClose={closeHistoricalDrawer}
              position="right"
              size={"90%"}
              withCloseButton={false}
            >
              {/* Fixed viewport height (minus the drawer body padding) so the
                  table pane inside gets a bounded box and scrolls on its own —
                  same pattern as DeviceDetailDrawer. */}
              <div className="flex h-[calc(100dvh-2rem)] w-full flex-col">
                <HistoricalDataTable
                  key={type}
                  item={itemQuery.data}
                  type={type === t("items.refill") ? "refill" : "scan"}
                  onClose={closeHistoricalDrawer}
                  headerStart={
                    <>
                      <Title order={3}>{t("common.historicalData")}</Title>
                      <SegmentedControl
                        data={[t("items.scan"), t("items.refill")]}
                        size="xs"
                        onChange={setType}
                      />
                    </>
                  }
                />
              </div>
            </Drawer>
          </Accordion>
        </form>
      </div>

      <Modal
        opened={templateModalOpened}
        onClose={closeTemplateModal}
        centered
        title={t("items.saveAsTemplate")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col gap-4">
          <TextInput
            label={t("items.templateName")}
            withAsterisk
            placeholder={t("templates.form.templateNamePlaceholder")}
            value={templateName}
            onChange={(e) => setTemplateName(e.currentTarget.value)}
          />

          <Textarea
            label={t("common.description")}
            placeholder={t("templates.form.descriptionPlaceholder")}
            rows={3}
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.currentTarget.value)}
          />

          <div className="w-full grid grid-cols-2 gap-2 mt-2">
            <Button
              variant="outline"
              color="black"
              onClick={closeTemplateModal}
            >
              {t("common.cancel")}
            </Button>
            <Button
              color="blue"
              disabled={!templateName}
              loading={createAndSaveAsTemplateMutation.isPending}
              onClick={() => {
                const payload = toSavePayload(form.values, "create");
                createAndSaveAsTemplateMutation.mutate({
                  itemPayload: payload as CreateItemPayload,
                  templateName,
                  templateDescription,
                });
              }}
            >
              {t("common.confirm")}
            </Button>
          </div>
        </div>
      </Modal>
      <HawbLinkWarningsModal
        opened={hawbConfirm !== null}
        warnings={hawbConfirm?.warnings ?? []}
        loading={hawbLinkBusy}
        onCancel={() => {
          const pending = hawbConfirm;
          setHawbConfirm(null);
          pending?.onCancel();
        }}
        onConfirm={() => {
          const pending = hawbConfirm;
          if (!pending) return;
          void applyHawbLink(pending.itemId, pending.hawbId).then((ok) => {
            setHawbConfirm(null);
            if (ok) pending.onDone();
            else pending.onCancel();
          });
        }}
      />
    </DetailPageLayout>
  );
};

export default ItemDetailPage;
